import { useQuery } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { SidebarSectionRow } from '@/components/blocks/Layout/Sidebar/SidebarSectionRow';
import { Text } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/EmptyState';
import { XStack } from '@/components/ui/layout/layout';
import { ResizableBox } from '@/components/ui/ResizableBox';
import { Markdown } from '@/components/ui/smoothdown';
import { cn } from '@/lib/utils';
import { Panel } from '@/stores/ui.store';

import { GraphView } from './GraphView';
import { KGAgentChatSidebar } from './KGAgentChatSidebar';
import {
  getWorldModelDocQuery,
  getWorldModelDocsQuery,
  type WorldModelDocListItem,
} from './world-model.query';

const worldModelRoute = getRouteApi('/_authenticated/world-model/');

type ViewMode = 'document' | 'graph';

function DocContainer({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className="flex justify-center w-full h-full overflow-y-auto">
      <div
        className={cn(
          'flex flex-col gap-8 px-6 py-8 max-w-4xl w-full',
          className
        )}
      >
        {children}
      </div>
    </div>
  );
}

const WorldModelDocContent = ({ selectedId }: { selectedId: string }) => {
  const {
    data: doc,
    isLoading,
    isError,
    error,
  } = useQuery(getWorldModelDocQuery(selectedId));

  if (isLoading)
    return (
      <DocContainer className="text-foreground-muted">Loading...</DocContainer>
    );
  if (isError)
    return (
      <DocContainer className="text-red-500">
        Error: {(error as Error).message}
      </DocContainer>
    );
  if (!doc)
    return (
      <DocContainer>
        <Empty.Root variant="plain">
          <Empty.Media variant="icon">
            <Icons.write />
          </Empty.Media>
          <Empty.Header>
            <Empty.Title>No document found</Empty.Title>
            <Empty.Description>
              This document may have been removed or is not yet available.
            </Empty.Description>
          </Empty.Header>
        </Empty.Root>
      </DocContainer>
    );

  return (
    <DocContainer className="pt-8 pb-0">
      <Markdown id="world-model" content={doc.content} />
      <div className="h-32 shrink-0" aria-hidden="true" />
    </DocContainer>
  );
};

export const WorldModelDocScreen = () => {
  const navigate = useNavigate();
  const { id } = worldModelRoute.useSearch();

  const [isDocSidebarOpen, setIsDocSidebarOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('document');

  const {
    data: listData,
    isLoading: isListLoading,
    isError: isListError,
    error: listError,
  } = useQuery(getWorldModelDocsQuery);

  const items = useMemo(() => {
    if (!listData) return [] as WorldModelDocListItem[];
    return [...listData].sort((a, b) => {
      const aTime = new Date(a.updated_at).getTime();
      const bTime = new Date(b.updated_at).getTime();
      return bTime - aTime;
    });
  }, [listData]);

  const isLatestDoc = items.length > 0 && id === items[0].id;

  // Reset to document view when switching away from latest doc
  useEffect(() => {
    if (!isLatestDoc) {
      setViewMode('document');
    }
  }, [isLatestDoc]);

  // Auto-select the latest doc if none selected
  useEffect(() => {
    if (!id && items.length > 0 && !isListLoading) {
      navigate({
        to: '/world-model',
        search: { id: items[0].id },
        replace: true,
      });
    }
  }, [id, items, isListLoading, navigate]);

  const selectItem = useCallback(
    (item: WorldModelDocListItem) => {
      navigate({ to: '/world-model', search: { id: item.id } });
    },
    [navigate]
  );

  const renderRow = useCallback(
    (item: WorldModelDocListItem) => {
      const isSelected = item.id === id;
      const updatedDate = new Date(item.updated_at);
      const isToday = updatedDate.toDateString() === new Date().toDateString();
      const title = isToday
        ? 'Today'
        : updatedDate.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year:
              updatedDate.getFullYear() !== new Date().getFullYear()
                ? 'numeric'
                : undefined,
          });
      const subtitle = `Updated ${updatedDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })}`;

      return (
        <SidebarSectionRow
          key={item.id}
          item={item}
          title={title}
          subtitle={subtitle}
          isSelected={isSelected}
          onSelect={selectItem}
          panel={Panel.WorldModelDoc}
        />
      );
    },
    [id, selectItem]
  );

  const canShowChat = isLatestDoc && viewMode === 'document';

  return (
    <XStack className="h-full overflow-hidden">
      {/* Left sidebar - doc list (collapsible) */}
      <AnimatePresence>
        {isDocSidebarOpen && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 'auto', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="h-full"
          >
            <ResizableBox
              id="world-model-sidebar"
              initialWidth={280}
              minWidth={220}
              maxWidth={400}
              className="h-full border-r border-border"
            >
              <div className="h-full w-full flex flex-col">
                {/* Sidebar header with close button */}
                <XStack className="items-center justify-between px-4 py-2 border-b border-border">
                  <Text
                    size="sm"
                    weight="medium"
                    className="text-foreground-muted"
                  >
                    Documents
                  </Text>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsDocSidebarOpen(false)}
                    title="Close sidebar"
                  >
                    <Icons.cancel />
                  </Button>
                </XStack>

                <div className="flex-1 overflow-y-auto px-2 py-2">
                  {isListLoading ? (
                    <div className="px-3 py-2 text-foreground-muted text-sm">
                      Loading...
                    </div>
                  ) : isListError ? (
                    <div className="px-3 py-2 text-red-500 text-sm">
                      Error: {(listError as Error).message}
                    </div>
                  ) : items.length === 0 ? (
                    <div className="px-3 py-2 text-foreground-muted text-sm">
                      No documents found.
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {items.map((item) => renderRow(item))}
                    </div>
                  )}
                </div>
              </div>
            </ResizableBox>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header bar */}
        <XStack className="items-center gap-2 px-2 py-1.5 border-b border-border">
          {/* Show hamburger only when sidebar is closed */}
          {!isDocSidebarOpen && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsDocSidebarOpen(true)}
              title="Show sidebar"
            >
              <Icons.drawer />
            </Button>
          )}

          {/* Segmented control - only on latest doc */}
          {isLatestDoc && (
            <XStack className="gap-0.5 bg-secondary rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('document')}
                className={cn(
                  'px-3 py-1 text-sm rounded-md transition-colors',
                  viewMode === 'document'
                    ? 'bg-surface text-foreground shadow-sm'
                    : 'text-foreground-muted hover:text-foreground'
                )}
              >
                Document
              </button>
              <button
                onClick={() => setViewMode('graph')}
                className={cn(
                  'px-3 py-1 text-sm rounded-md transition-colors',
                  viewMode === 'graph'
                    ? 'bg-surface text-foreground shadow-sm'
                    : 'text-foreground-muted hover:text-foreground'
                )}
              >
                Graph
              </button>
            </XStack>
          )}

          <div className="flex-1" />

          {/* Chat toggle - only on latest doc in document view AND chat is closed */}
          {canShowChat && !isChatOpen && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsChatOpen(true)}
              title="Show chat"
            >
              <Icons.newChat />
            </Button>
          )}
        </XStack>

        {/* Content area */}
        <div className="flex-1 min-h-0 relative">
          <AnimatePresence mode="wait">
            {id && viewMode === 'document' ? (
              <motion.div
                key="document"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="h-full"
              >
                <WorldModelDocContent selectedId={id} />
              </motion.div>
            ) : (
              <motion.div
                key="graph"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="h-full"
              >
                <GraphView />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Right sidebar - KG Agent chat (only visible in document view) */}
      <AnimatePresence>
        {canShowChat && isChatOpen && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 'auto', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="h-full"
          >
            <KGAgentChatSidebar onClose={() => setIsChatOpen(false)} />
          </motion.div>
        )}
      </AnimatePresence>
    </XStack>
  );
};
